import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { centralMonitorData } from "../../Actions/CentralMonitoringScreenAction";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
// import refresh from "../../assets/images/refresh.svg";
import schoolIcon from "../../assets/images/schoolIcon.svg";
import filterIcon from "../../assets/images/filterIcon.svg";
import CommonTable from "../Table"; // ✅ use CommonTable instead of MUI Table
import "./ListView.scss";

const statusColors = {
  Healthy: "success",
  Critical: "error",
};

const ListView = () => {
  const [search, setSearch] = useState("");
  const [toggleBtn, setToggleBtn] = useState(false);
  const [filter, setFilter] = useState("View all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { dashboardData, loading } = useSelector(
    (state) => state.CentralMonitoringScreenReducer
  );
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(centralMonitorData());
  }, [dispatch]);

  useEffect(() => {
    if (dashboardData.length > 0) {
      setLastUpdateTime(new Date());
    }
  }, [dashboardData]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleFilter = (status) => {
    setFilter(status);
  };

  const handleRefresh = () => {
    dispatch(centralMonitorData());
    setLastUpdateTime(new Date());
  };

  const handleStatusClick = (row) => {
    navigate(`/healthStatus/${row.id}`);
  };

  const filteredData = dashboardData.filter((item) => {
  const matchesSearch =
    item.customerName?.toLowerCase().includes(search.toLowerCase()) ||
    item.location?.toLowerCase().includes(search.toLowerCase()) ||
    item.address?.toLowerCase().includes(search.toLowerCase());

  // Use healthStatus or a derived status instead of item.status
  const derivedStatus = !item.healthStatus ? "Critical" : "Healthy";

  const matchesFilter = filter === "View all" || derivedStatus === filter;
  return matchesSearch && matchesFilter;
});

  // Sort the filtered data to prioritize buildings with lockdown initiated
  const sortedFilteredData = [...filteredData].sort((a, b) => {
    const getPriority = (item) => {
      if (item.lockdown === true && item.drillMode === true) return 4;
      if (item.lockdown === true && item.testMode === true) return 3;
      if (item.lockdown === true) return 2;
      if (item.drillMode === true) return 1.5;
      if (item.testMode === true) return 1;
      return 0;
    };
    return getPriority(b) - getPriority(a);
  });

  const paginatedData = sortedFilteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const drptoggler = () => {
    setToggleBtn((prev) => !prev);
  };

  const isMobile = window.innerWidth <= 768;

  const getStatusTabClass = () => {
    if (isMobile && toggleBtn) return "statustabs show";
    if (isMobile && !toggleBtn) return "statustabs notShow";
    return "statustabs";
  };

  const renderLockdownStatus = (row) => {
    let statusText = "Inactive";
    let indicatorClass;
    let pulseClass = "lockdown-stable";
    if (row.lockdown === true && row.drillMode === true) {
      statusText = "Active (Drill Mode)";
      indicatorClass = "lockdown-active";
      pulseClass = "lockdown-pulse";
    } else if (row.lockdown === false && row.drillMode === true) {
      statusText = "Inactive (Drill Mode)";
      indicatorClass = "lockdown-inactive";
    } else if (row.lockdown === false && row.testMode === true) {
      statusText = "Inactive (Test Mode)";
      indicatorClass = "lockdown-inactive";
    } else if (row.lockdown === true && row.testMode === true) {
      statusText = "Active (Test Mode)";
      indicatorClass = "lockdown-active";
      pulseClass = "lockdown-pulse";
    } else if (row.lockdown === true) {
      statusText = "Active";
      indicatorClass = "lockdown-active";
      pulseClass = "lockdown-pulse";
    } else {
      indicatorClass = "lockdown-inactive";
    }

    return (
      <div className={`lockdown-status-container  statusWrapper_${indicatorClass} `}>
        <div className={`lockdown-indicator ${indicatorClass}`}>
          <span className={pulseClass}></span>
        </div>
        <span className="lockdown-text">{statusText}</span>
      </div>
    );
  };

  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    // Delay to ensure NVDA catches it after initial DOM render
    const timer = setTimeout(() => {
      setAnnouncement(
        `Showing ${filteredData.length} Results.`
      );
    }, 500); // 0.5s delay is safe for NVDA

    return () => clearTimeout(timer);
  }, []); // run once on mount

  useEffect(() => {
    // Update on data/filter changes
    if(filter === "View all") {
        setAnnouncement(
        `Showing ${filteredData.length} Results.`
      );
    } else {
      setAnnouncement(
      `Showing ${filteredData.length} Results for health status ${filter}.`
    );
    }
  }, [filteredData.length, filter]);

  // CommonTable columns
  const columns = [
    {
      field: "status",
      headerName: "Status",
      sortable: false,
      renderCell: ({ row }) => (
        <button
          type="button"
          className="status-cell"
          onClick={() => handleStatusClick(row)}
          style={{
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
            color: "inherit",
          }}
        >
          <span
            className={
              statusColors[!row.healthStatus ? "Critical" : "Healthy"] || ""
            }
          >
            {!row.healthStatus ? "Critical" : "Healthy"}
          </span>
        </button>
      ),
    },
    { field: "customerName", headerName: "Customer Name", sortable: true },
    {
      field: "building",
      headerName: "Building",
      sortable: false,
      renderCell: () => (
        <span>
          <a href="#" aria-hidden tabIndex={-1}>
            <img src={schoolIcon} alt="School Icon" />
          </a>{" "}
          School
        </span>
      ),
    },
    {
      field: "location",
      headerName: "Location",
      sortable: false,
      renderCell: ({ row }) => {
      const { latitude, longitude } = row;
      const mapUrl =
        latitude && longitude
          ? `https://www.google.com/maps?q=${latitude},${longitude}`
          : "#";
      return <span>
            <a href={mapUrl} className="link-btn" aria-label={`${row.location} open in new tab`} target="_blank">{row.location}</a>
        </span>},
    },
    { field: "address", headerName: "Address", sortable: true },
    {
      field: "lockdownStatus",
      headerName: "Lockdown Status",
      sortable: false,
      renderCell: ({ row }) => renderLockdownStatus(row),
    },
  ];

  const rows = paginatedData.map((row) => ({
    ...row,
    building: "School",
    lockdownStatus: row.lockdown,
  }));

  return (
    <div className="centralmointor_wrapper">
      {/* <span className="sr-only" >
        {announcement}
      </span> */}
      <div className="innerWrapper cntm_inner_wrapper">
        <div className="statusTab_wrapper">
          <Row>
            <Col xs={8} md={6} xl={4}>
              <div className="seachField">
                <TextField
                  fullWidth
                  placeholder="Search"
                  variant="outlined"
                  value={search}
                  onChange={handleSearch}
                />
              </div>
            </Col>
            <Col xs={4} md={6} xl={8}>
              {isMobile && (
                <div
                  className="filter-dropdown-container"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    position: "relative",
                  }}
                >
                  <button
                    className="d-block d-md-none filterBtn"
                    onClick={() => drptoggler()}
                  >
                    <img src={filterIcon} alt="Filter" /> Filter
                  </button>
                </div>
              )}
              <div className={getStatusTabClass()}>
                <div className="tabs_btn_wrapper" role="list">
                  {["View all", "Healthy", "Critical"].map(
                    (status) => (
                      <div role="listitem" style={{ display: 'flex' }}>
                        <Button
                          key={status}
                          variant={filter === status ? "contained" : "outlined"}
                          className="tabsButton"
                          onClick={() => handleFilter(status)}
                        >
                          {status}
                        </Button>
                      </div>
                    )
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div className="refeshandRes_wrapper">
          <Row>
            <Col xs={6} xl={6}>
              <div className="resultWrapper">
                <Typography component="p" variant="body1" aria-label="" refeshandRes_wrapper>
                  {filteredData.length} Results
                </Typography>
              </div>
            </Col>
            {/* <Col xs={6} xl={6}>
              <div className="refrshWrapper">
                <button
                  onClick={handleRefresh}
                  className={loading ? "rotate" : ""}
                >
                  <img src={refresh} alt="" /> Refresh
                </button>
              </div>
            </Col> */}
          </Row>
        </div>

        <div className="monitioring_table">
          {dashboardData.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <CircularProgress />
            </div>
          ) : (
            <CommonTable
              columns={columns}
              rows={rows}
              page={page + 1} // CommonTable is 1-based
              totalPages={Math.ceil(filteredData.length / rowsPerPage)}
              onPageChange={(newPage) => setPage(newPage - 1)} // convert back to 0-based
              pageSize={rowsPerPage}
              // onSortChange={(config) => {
              //   console.log("Sort change:", config);
              // }}
              // onRowClick={(row) => {
              //   console.log("Row clicked:", row);
              // }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ListView;
